import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, MailCheck, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API_BASE } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';

const msg = (e, f) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : Array.isArray(d) ? d.map(x => x.msg).join(' ') : f; };
const Shell = ({ children, testid }) => (
  <div className="min-h-screen bg-background grid place-items-center p-6">
    <div className="w-full max-w-md bg-card border border-border rounded-3xl p-8 shadow-sm space-y-5" data-testid={testid}>{children}</div>
  </div>
);

export function VerifyEmailPending() {
  const { user, logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const resend = async () => {
    setBusy(true);
    try { await axios.post(`${API_BASE}/auth/resend-verification`); toast.success('Te reenviamos el email'); }
    catch (e) { toast.error(msg(e, 'No se pudo reenviar')); }
    finally { setBusy(false); }
  };
  return (
    <Shell testid="verify-email-pending">
      <MailCheck className="w-10 h-10 text-primary" />
      <h1 className="font-heading text-2xl font-bold">Confirmá tu email</h1>
      <p className="text-sm text-muted-foreground">Te enviamos un enlace a <strong>{user?.email}</strong>. Vence en 24 horas y se usa una sola vez. Hasta confirmarlo no podés usar el panel.</p>
      <div className="flex flex-wrap gap-2">
        <Button onClick={resend} disabled={busy} className="rounded-full" data-testid="resend-verification-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Reenviar email</Button>
        <Button variant="ghost" onClick={logout} data-testid="verify-logout-btn"><LogOut className="w-4 h-4 mr-2" />Salir</Button>
      </div>
    </Shell>
  );
}

export function VerifyEmail() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const sent = useRef(null);
  useEffect(() => {
    // El token es de un solo uso: evitar el doble envío de StrictMode
    if (sent.current === token) return;
    sent.current = token;
    axios.post(`${API_BASE}/auth/verify-email`, { token })
      .then(() => setState({ ok: true }))
      .catch(e => setState({ error: msg(e, 'El enlace no es válido') }));
  }, [token]);
  return (
    <Shell testid="verify-email-page">
      {state.loading && <Loader2 className="w-6 h-6 animate-spin" />}
      {state.ok && <><h1 className="font-heading text-2xl font-bold" data-testid="verify-email-success">¡Email confirmado!</h1><Button asChild className="rounded-full"><a href="/dashboard" data-testid="verify-go-dashboard">Ir al panel</a></Button></>}
      {state.error && <p className="text-red-600" data-testid="verify-email-error">{state.error}</p>}
    </Shell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try { const { data } = await axios.post(`${API_BASE}/auth/forgot-password`, { email }); setDone(data.message); }
    catch (err) { toast.error(msg(err, 'No se pudo enviar')); }
  };
  return (
    <Shell testid="forgot-password-page">
      <h1 className="font-heading text-2xl font-bold">Recuperar contraseña</h1>
      {done ? <p className="text-sm" data-testid="forgot-done">{done}</p> : (
        <form onSubmit={submit} className="space-y-3">
          <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" data-testid="forgot-email-input" />
          <Button type="submit" className="w-full rounded-full" data-testid="forgot-submit-btn">Enviar enlace</Button>
        </form>
      )}
      <Link to="/login" className="text-sm underline">Volver a ingresar</Link>
    </Shell>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    try { await axios.post(`${API_BASE}/auth/reset-password`, { token, password }); toast.success('Contraseña actualizada'); navigate('/login'); }
    catch (err) { toast.error(msg(err, 'No se pudo cambiar')); }
  };
  return (
    <Shell testid="reset-password-page">
      <h1 className="font-heading text-2xl font-bold">Nueva contraseña</h1>
      <form onSubmit={submit} className="space-y-3">
        <Label>Mínimo 8 caracteres, con letras y números</Label>
        <Input type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)} data-testid="reset-password-input" />
        <Button type="submit" className="w-full rounded-full" data-testid="reset-submit-btn">Guardar</Button>
      </form>
    </Shell>
  );
}

export function LegalPage({ doc }) {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API_BASE}/public/legal/${doc}`).then(r => setD(r.data)).catch(() => {}); }, [doc]);
  return (
    <div className="min-h-screen bg-background px-6 py-12">
      <article className="max-w-3xl mx-auto" data-testid={`legal-page-${doc}`}>
        <h1 className="font-heading text-3xl font-bold mb-2">{d?.title}</h1>
        <p className="text-xs text-muted-foreground mb-8">Versión {d?.version} · {d && new Date(d.updated_at).toLocaleDateString('es-AR')}</p>
        <div className="whitespace-pre-wrap leading-relaxed text-sm" data-testid="legal-content">{d?.content}</div>
      </article>
    </div>
  );
}
